import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Box,
  IconButton,
  Tooltip,
  InputAdornment,
  Alert,
  Menu,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  Typography
} from '@mui/material';

import { createTheme, ThemeProvider, useTheme } from '@mui/material/styles';
import {
  HelpCircle as HelpIcon,
  Search as SearchIcon,
  MoreVertical,
  Trash2,
  MessageCircle
} from 'lucide-react';

import chatService from '../services/AGTUps-product-services/chatService';
import { toast } from 'react-toastify';

/**
 * Paleta de colores y theme personalizado
 */
const COLORS = {
  primary: 'rgba(128, 128, 128, 0.5)',
  text: {
    light: '#FFFFFF',
    dark: '#FFFFFF'
  }
};

const createCustomTheme = (mode, actualMode) =>
  createTheme({
    palette: {
      mode,
      actualMode
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: '12px',
            textTransform: 'none',
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none'
            }
          },
          contained: {
            backgroundColor: actualMode === 'wallpaper' ? '#FFFFFF' : COLORS.primary,
            color: actualMode === 'wallpaper' ? '#000000' : '#ffffff',
            '&:hover': {
              backgroundColor: actualMode === 'wallpaper' ? 'rgba(255, 255, 255, 0.85)' : COLORS.primary
            },
            '&:disabled': {
              backgroundColor:
                mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.12)'
                  : 'rgba(0, 0, 0, 0.12)'
            }
          },
          text: {
            color: actualMode === 'wallpaper' ? '#FFFFFF' : COLORS.text[mode]
          }
        }
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: '16px',
            border: '1px solid',
            borderColor: actualMode === 'wallpaper'
              ? 'rgba(128, 128, 128, 0.3)'
              : (mode === 'light' ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.12)'),
            backgroundColor: actualMode === 'wallpaper' ? 'rgba(128, 128, 128, 0.3)' : undefined,
            backdropFilter: actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
            WebkitBackdropFilter: actualMode === 'wallpaper' ? 'blur(10px)' : 'none'
          }
        }
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: '12px',
              backgroundColor: actualMode === 'wallpaper' ? 'rgba(128, 128, 128, 0.25)' : undefined,
              backdropFilter: actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
              WebkitBackdropFilter: actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
              '& fieldset': {
                borderColor: actualMode === 'wallpaper' ? 'rgba(128, 128, 128, 0.3)' : undefined
              },
              '& input, & textarea': {
                color: actualMode === 'wallpaper' ? '#FFFFFF' : undefined
              }
            },
            '& .MuiInputLabel-root': {
              color: actualMode === 'wallpaper' ? 'rgba(255, 255, 255, 0.7)' : undefined
            }
          }
        }
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            borderRadius: '12px',
            '&.Mui-selected': {
              backgroundColor: actualMode === 'wallpaper' ? '#FFFFFF' : COLORS.primary,
              color: actualMode === 'wallpaper' ? '#000000' : '#ffffff',
              '&:hover': {
                backgroundColor: actualMode === 'wallpaper' ? 'rgba(255, 255, 255, 0.85)' : COLORS.primary
              }
            }
          }
        }
      },
      MuiListItem: {
        styleOverrides: {
          root: {
            borderRadius: '12px',
            border: '1px solid',
            borderColor: actualMode === 'wallpaper'
              ? 'rgba(128, 128, 128, 0.3)'
              : (mode === 'light' ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.12)'),
            backgroundColor: actualMode === 'wallpaper' ? 'rgba(128, 128, 128, 0.25)' : undefined,
            backdropFilter: actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
            WebkitBackdropFilter: actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
            marginBottom: '8px',
            '&:hover': {
              backgroundColor: actualMode === 'wallpaper'
                ? 'rgba(128, 128, 128, 0.35)'
                : (mode === 'light' ? 'rgba(128, 128, 128, 0.04)' : 'rgba(128, 128, 128, 0.08)')
            }
          }
        }
      }
    },
    typography: {
      h6: {
        fontWeight: 600
      },
      button: {
        textTransform: 'none'
      }
    }
  });

/**
 * Componente principal "ActiveDealsShortcut" (o "ChatsShortcut").
 * - `open`: indica si el modal está abierto.
 * - `onClose`: callback para cerrar el modal.
 *
 * Muestra una lista de “active deals” o “chats” (vía chatService).
 */
const ActiveDealsShortcut = ({ open, onClose }) => {
  // Usamos el modo (light/dark) del theme padre:
  const parentTheme = useTheme();
  const customTheme = createCustomTheme(parentTheme.palette.mode, parentTheme.palette.actualMode);

  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Manejo menú contextual en cada “deal”
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedChat, setSelectedChat] = useState(null);

  // Cargar las "active deals" al abrir el modal
  useEffect(() => {
    if (open) {
      fetchActiveDeals();
    }
  }, [open]);

  const fetchActiveDeals = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await chatService.getUserChats();
      if (response.success) {
        setChats(response.data);
      } else {
        throw new Error('Error loading active deals.');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const filteredChats = chats.filter((chat) =>
    (chat.otherCompanyName || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Para abrir menú contextual
  const handleMenuOpen = (event, chat) => {
    setAnchorEl(event.currentTarget);
    setSelectedChat(chat);
  };

  // Cerrar menú contextual
  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedChat(null);
  };

  // Eliminar un "chat" (active deal)
  const handleDeleteChat = async (chatId) => {
    try {
      const result = await chatService.deleteChat(chatId);
      if (result.success) {
        setChats((prev) => prev.filter((item) => item.chatId !== chatId));
        toast.success('Deal/Chat deleted successfully');
      } else {
        toast.error('Error deleting deal/chat');
      }
    } catch (err) {
      console.error(err);
      toast.error('Error deleting deal/chat');
    } finally {
      handleMenuClose();
    }
  };

  return (
    <ThemeProvider theme={customTheme}>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="md"
        aria-labelledby="active-deals-dialog-title"
      >
        <DialogTitle
          id="active-deals-dialog-title"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pb: 2
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Active Deals (Chats)
          </Typography>
          <Tooltip title="Visualiza y gestiona tus acuerdos activos con proveedores, negociaciones, etc.">
            <IconButton size="small">
              <HelpIcon />
            </IconButton>
          </Tooltip>
        </DialogTitle>

        <DialogContent>
          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 2,
                borderRadius: '12px'
              }}
            >
              {error}
            </Alert>
          )}

          {/* Barra de búsqueda */}
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              placeholder="Search deals..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon size={20} />
                  </InputAdornment>
                )
              }}
            />
          </Box>

          {/* Listado de deals o chats */}
          {loading ? (
            <Box
              sx={{
                py: 4,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <List
              dense
              sx={{
                maxHeight: '360px',
                overflow: 'auto',
                '& .MuiListItem-root': {
                  cursor: 'default',
                  display: 'flex',
                  alignItems: 'center'
                }
              }}
            >
              {filteredChats.map((chat, index) => (
                <ListItem key={chat.chatId || index}>
                  <ListItemText
                    primary={
                      <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                        {chat.otherCompanyName}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="caption" color="text.secondary">
                        {chat.lastMessage || 'No messages yet.'}
                      </Typography>
                    }
                  />
                  <IconButton onClick={(e) => handleMenuOpen(e, chat)} size="small">
                    <MoreVertical size={20} />
                  </IconButton>
                </ListItem>
              ))}
            </List>
          )}

          {/* Menú contextual para cada deal/chat */}
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
            <MenuItem
              onClick={() => {
                if (selectedChat) handleDeleteChat(selectedChat.chatId);
              }}
              sx={{ color: 'error.main' }}
            >
              <Trash2 size={16} style={{ marginRight: 8 }} /> Delete
            </MenuItem>
          </Menu>
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={onClose}
            sx={{
              color: parentTheme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : COLORS.text[parentTheme.palette.mode]
            }}
          >
            Close
          </Button>
          <Button
            onClick={() => {
              toast.info('Future: add "Create Deal/Chat" flow here');
            }}
            variant="contained"
            startIcon={<MessageCircle size={18} />}
          >
            Add Deal
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default ActiveDealsShortcut;
